// Sloped and stepped ceilings (CIN-15417). Pure; relative imports only.
//
// A room's ceiling is flat at `room.height` unless its geometry carries a `CeilingProfile`: the height at the screen
// wall and at the back wall (a linear slope between them) plus optional steps. The height depends on the distance
// from the screen wall only (plan -y, world z), never on x.
//
// DECISIONS
// - Depth `d` is measured in mm from the screen wall (plan y = 0) toward the back wall (plan y = -depth); world z is
//   `d / 1000`. Anything in front of the screen wall (a curved screen wall's arc, a screen pulled forward) reads the
//   height at d = 0, anything behind the back wall the height at d = depth.
// - The slope runs from `screen` at d = 0 to `back` at d = depth, in front of the first step. A step `{ at, height }`
//   makes the ceiling flat at `height` from `at` mm on, up to the next step or the back wall. So a stepped ceiling is
//   `screen` up to the first step and then each step's height; with no steps it is the plain slope.
// - `room.height` stays the box: the highest point of the ceiling (the studio derives it, like width/depth from an
//   outline), so old readers keep a box that contains the room.
// - Steps jump: the shell draws a riser between the two heights. Wherever geometry is cut at a step, it is cut twice,
//   `CUT_EPS` in front of and behind it, so every piece in between has one linear height and nothing interpolates
//   across the jump.

import { intersectPolygons } from './polygon-clip.ts';
import type { CeilingProfile, OutlineError, PlanPoint, RoomGeometry } from './types.ts';
import type { WallRun, WorldPoint } from './wall-segments.ts';

export const CEILING_LIMITS = {
    minHeight: 2_000,
    maxHeight: 30_000,
    maxSteps: 8,
    /** The smallest distance between two steps, and between the screen wall and the first step, mm. */
    minStepGap: 500
} as const;

/** How far in front of and behind a step geometry is cut, m. */
export const CUT_EPS = 1e-4;

const isInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);
const inRange = (n: unknown): boolean => isInt(n) && n >= CEILING_LIMITS.minHeight && n <= CEILING_LIMITS.maxHeight;

/**
 * First problem with a ceiling profile, or null. Steps are not checked against the room's depth: a room made
 * shallower keeps its profile, and a step behind the back wall is simply not drawn (`ceilingSections`).
 */
export function validateCeiling(c: CeilingProfile): OutlineError | null {
    if (typeof c !== 'object' || c === null || !inRange(c.screen) || !inRange(c.back)) return 'bad-ceiling';
    if (c.steps === undefined) return null;
    if (!Array.isArray(c.steps) || c.steps.length > CEILING_LIMITS.maxSteps) return 'bad-ceiling';
    let previous = 0;
    for (const step of c.steps) {
        if (typeof step !== 'object' || step === null || !isInt(step.at) || !inRange(step.height)) return 'bad-ceiling';
        if (step.at - previous < CEILING_LIMITS.minStepGap) return 'bad-ceiling';
        previous = step.at;
    }
    return null;
}

/** The profile read off the wire: rounded to whole mm, steps sorted; null when absent or not valid. */
export function normalizeCeiling(raw: unknown): CeilingProfile | null {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
    const record = raw as Record<string, unknown>;
    const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
    if (!finite(record.screen) || !finite(record.back)) return null;
    const profile: CeilingProfile = { screen: Math.round(record.screen), back: Math.round(record.back) };
    if (Array.isArray(record.steps)) {
        const steps = record.steps.flatMap((s) =>
            typeof s === 'object' && s !== null && finite(s.at) && finite(s.height)
                ? [{ at: Math.round(s.at), height: Math.round(s.height) }]
                : []
        );
        if (steps.length !== record.steps.length) return null;
        if (steps.length > 0) profile.steps = steps.sort((a, b) => a.at - b.at);
    }
    return validateCeiling(profile) ? null : profile;
}

/**
 * The highest point of the ceiling as it is drawn in a room `depth` mm deep, whole mm: what `room.height` becomes.
 * With steps the slope ends at the first step short of `back`, and a step behind the back wall is not drawn.
 */
export function ceilingTop(c: CeilingProfile, depth: number): number {
    return Math.round(
        ceilingRange({ height: 0, depth, geometry: { version: 1, outline: null, levels: [], ceiling: c } }).max
    );
}

/** The profile with every height scaled so its top (`ceilingTop`) is `top`, each held to the limits. Steps keep their place. */
export function scaleCeiling(c: CeilingProfile, depth: number, top: number): CeilingProfile {
    const k = top / Math.max(1, ceilingTop(c, depth));
    const scale = (h: number) =>
        Math.min(CEILING_LIMITS.maxHeight, Math.max(CEILING_LIMITS.minHeight, Math.round(h * k)));
    const scaled: CeilingProfile = { screen: scale(c.screen), back: scale(c.back) };
    if (c.steps) scaled.steps = c.steps.map((step) => ({ at: step.at, height: scale(step.height) }));
    return scaled;
}

/** The lowest ceiling height (mm) over the plan y range between `yA` and `yB` (mm). */
export function ceilingMinOver(
    room: { height: number; depth: number; geometry?: RoomGeometry | null },
    yA: number,
    yB: number
): number {
    const [d0, d1] = [Math.min(-yA, -yB), Math.max(-yA, -yB)];
    const sections = ceilingSections(room);
    const heights = [heightInSections(sections, d0), heightInSections(sections, d1)];
    sections.forEach((s, i) => {
        // A section reaches over the range when it starts before its end and ends (where the next starts) after its start.
        const end = sections[i + 1]?.d0 ?? Infinity;
        if (s.d0 > d1 || end < d0) return;
        const at = (d: number) => {
            const span = s.d1 - s.d0;
            return s.h0 + (s.h1 - s.h0) * (span > 0 ? Math.min(1, Math.max(0, (d - s.d0) / span)) : 0);
        };
        heights.push(at(Math.max(d0, s.d0)), at(Math.min(d1, s.d1)));
    });
    return Math.min(...heights);
}

/** A stretch of the ceiling from `d0` to `d1` mm from the screen wall over which its height runs linearly. */
export type CeilingSection = { d0: number; d1: number; h0: number; h1: number };

/** The room's ceiling as sections front to back (one flat section without a profile). */
export function ceilingSections(room: {
    height: number;
    depth: number;
    geometry?: RoomGeometry | null;
}): CeilingSection[] {
    const c = room.geometry?.ceiling;
    const depth = Math.max(1, room.depth);
    if (!c) return [{ d0: 0, d1: depth, h0: room.height, h1: room.height }];
    const steps = (c.steps ?? []).filter((s) => s.at > 0 && s.at < depth);
    const slopeEnd = steps[0]?.at ?? depth;
    const sections: CeilingSection[] = [
        { d0: 0, d1: slopeEnd, h0: c.screen, h1: c.screen + ((c.back - c.screen) * slopeEnd) / depth }
    ];
    steps.forEach((step, i) => {
        const d1 = steps[i + 1]?.at ?? depth;
        sections.push({ d0: step.at, d1, h0: step.height, h1: step.height });
    });
    return sections;
}

/** Height of the ceiling (mm above the main floor) at `d` mm from the screen wall. */
export function heightInSections(sections: readonly CeilingSection[], d: number): number {
    let section = sections[0];
    for (const candidate of sections) if (d >= candidate.d0) section = candidate;
    const span = section.d1 - section.d0;
    const t = span > 0 ? Math.min(1, Math.max(0, (d - section.d0) / span)) : 0;
    return section.h0 + (section.h1 - section.h0) * t;
}

/** Height of the room's ceiling (mm above the main floor) over the plan point at plan `y` (mm). */
export function ceilingHeightAt(
    room: { height: number; depth: number; geometry?: RoomGeometry | null },
    planY: number
): number {
    return heightInSections(ceilingSections(room), -planY);
}

/** The lowest and highest point of the room's ceiling, mm. */
export function ceilingRange(room: { height: number; depth: number; geometry?: RoomGeometry | null }): {
    min: number;
    max: number;
} {
    const heights = ceilingSections(room).flatMap((s) => [s.h0, s.h1]);
    return { min: Math.min(...heights), max: Math.max(...heights) };
}

/** The ceiling in world space (meters, y up, z = d / 1000): what the 3D shell and the features draw against. */
export type CeilingWorld = {
    /** False for the plain flat ceiling. */
    shaped: boolean;
    /** World y of the ceiling over world z. */
    at: (z: number) => number;
    /** World z of every step and of the end of the slope (where the height jumps or bends), ascending. */
    breaks: number[];
    /** Sections in world units: z0..z1 with heights y0..y1 (the first open to the front, the last to the back). */
    sections: { z0: number; z1: number; y0: number; y1: number }[];
    min: number;
    max: number;
};

export function ceilingWorld(
    room: { height: number; depth: number; geometry?: RoomGeometry | null },
    floorY: number
): CeilingWorld {
    const raw = ceilingSections(room);
    const sections = raw.map((s) => ({
        z0: s.d0 / 1000,
        z1: s.d1 / 1000,
        y0: floorY + s.h0 / 1000,
        y1: floorY + s.h1 / 1000
    }));
    const ys = sections.flatMap((s) => [s.y0, s.y1]);
    return {
        shaped: !!room.geometry?.ceiling,
        at: (z) => floorY + heightInSections(raw, z * 1000) / 1000,
        breaks: breaksOf(sections),
        sections,
        min: Math.min(...ys),
        max: Math.max(...ys)
    };
}

/**
 * Where the ceiling jumps or bends: every step, plus the screen wall and the back wall where a slope meets the flat
 * height in front of and behind the room.
 */
function breaksOf(sections: CeilingWorld['sections']): number[] {
    const first = sections[0];
    const last = sections[sections.length - 1];
    const breaks = sections.slice(1).map((s) => s.z0);
    if (first.y0 !== first.y1) breaks.unshift(first.z0);
    if (last.y0 !== last.y1) breaks.push(last.z1);
    return breaks;
}

/** dy/dz of the ceiling at world z (0 where it holds flat: in front of the screen wall, behind the back wall, on a step). */
export function ceilingSlopeAt(ceiling: CeilingWorld, z: number): number {
    const { sections } = ceiling;
    if (z < sections[0].z0 || z > sections[sections.length - 1].z1) return 0;
    let section = sections[0];
    for (const candidate of sections) if (z >= candidate.z0) section = candidate;
    return section.z1 > section.z0 ? (section.y1 - section.y0) / (section.z1 - section.z0) : 0;
}

/** How far the ceiling is above a camera at world (y, z), m: below zero once the camera is above it. */
export function ceilingClearance(ceiling: CeilingWorld, camera: { y: number; z: number }): number {
    return ceiling.at(camera.z) - camera.y;
}

/** Where the segment a -> b (world) is cut for the ceiling: parameters 0 < t < 1, `CUT_EPS` around every break. */
export function ceilingCuts(ceiling: CeilingWorld, a: WorldPoint, b: WorldPoint): number[] {
    const dz = b.z - a.z;
    const length = Math.hypot(b.x - a.x, dz);
    if (Math.abs(dz) < 1e-9 || length < 1e-9) return [];
    const ts: number[] = [];
    for (const z of ceiling.breaks) {
        const t = (z - a.z) / dz;
        const eps = CUT_EPS / length;
        for (const cut of [t - eps, t + eps]) if (cut > 0 && cut < 1) ts.push(cut);
    }
    return ts.sort((x, y) => x - y);
}

/** Meters along a wall run where the ceiling over it breaks (see `ceilingCuts`), ascending. */
export function runCeilingCuts(run: WallRun, ceiling: CeilingWorld): number[] {
    if (ceiling.breaks.length === 0) return [];
    return run.segments.flatMap((seg) => ceilingCuts(ceiling, seg.a, seg.b).map((t) => seg.u0 + (seg.u1 - seg.u0) * t));
}

/**
 * A plan polygon (mm) cut into the pieces that lie under one ceiling section each: the polygon itself for a ceiling
 * without breaks. Pieces are counter-clockwise.
 */
export function ceilingPieces(ring: readonly PlanPoint[], ceiling: CeilingWorld): PlanPoint[][] {
    if (ceiling.breaks.length === 0) return [ring.slice()];
    const xs = ring.map((p) => p.x);
    const ys = ring.map((p) => p.y);
    const x0 = Math.min(...xs) - 1000;
    const x1 = Math.max(...xs) + 1000;
    const bottom = Math.min(...ys) - 1000;
    const top = Math.max(...ys) + 1000;
    // World z = -plan y / 1000: a break at z is the plan line y = -z * 1000.
    const lines = [top, ...ceiling.breaks.map((z) => -z * 1000), bottom];
    const pieces: PlanPoint[][] = [];
    for (let i = 0; i + 1 < lines.length; i += 1) {
        const band = [
            { x: x0, y: lines[i + 1] },
            { x: x1, y: lines[i + 1] },
            { x: x1, y: lines[i] },
            { x: x0, y: lines[i] }
        ];
        pieces.push(...intersectPolygons(ring, band));
    }
    return pieces;
}

/**
 * The ceiling over one of `ceilingPieces`: its height at world z, held to the piece's own side of every break (a
 * vertex on a step reads the height of the piece it belongs to), and its slope dy/dz.
 */
export function pieceSurface(
    ceiling: CeilingWorld,
    piece: readonly PlanPoint[]
): { y: (z: number) => number; slope: number } {
    const midZ = -piece.reduce((sum, p) => sum + p.y, 0) / Math.max(1, piece.length) / 1000;
    const bounds = [-Infinity, ...ceiling.breaks, Infinity];
    const band = Math.max(
        0,
        bounds.findIndex((z, i) => midZ >= z && midZ <= bounds[i + 1])
    );
    const lo = bounds[band] + CUT_EPS;
    const hi = bounds[band + 1] - CUT_EPS;
    const clamp = (z: number) => Math.min(hi, Math.max(lo, z));
    const za = clamp(midZ - 0.1);
    const zb = clamp(midZ + 0.1);
    return {
        y: (z) => ceiling.at(clamp(z)),
        slope: zb > za ? (ceiling.at(zb) - ceiling.at(za)) / (zb - za) : 0
    };
}

/** The x ranges where the plan polygon crosses the plan line y (mm), sorted. */
export function ringIntervals(ring: readonly PlanPoint[], y: number): [number, number][] {
    const xs: number[] = [];
    for (let i = 0; i < ring.length; i += 1) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        if (a.y > y !== b.y > y) xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
    }
    xs.sort((a, b) => a - b);
    const out: [number, number][] = [];
    for (let i = 0; i + 1 < xs.length; i += 2) out.push([xs[i], xs[i + 1]]);
    return out;
}
